import React, { useState, useEffect } from "react";
import { api } from "../../../../lib/api";
import { toast } from "sonner";

export function TransactionsPanel() {
  const [data, setData] = useState(null);
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [kind, setKind] = useState("all");
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    api.get("/admin/transactions-summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (since) params.set("since", since);
      if (until) params.set("until", until);
      if (kind && kind !== "all") params.set("kind", kind);
      const qs = params.toString();

      const t = await api.post(`/admin/transactions-download-token${qs ? `?${qs}` : ""}`);
      const token = t.data?.token;
      if (!token) throw new Error("no token");

      const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, "") || "";
      const finalQs = qs ? `${qs}&token=${encodeURIComponent(token)}` : `token=${encodeURIComponent(token)}`;
      const url = `${base}/api/admin/transactions.csv?${finalQs}`;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate download");
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return null;
  const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);
  const tiles = [
    { key: "today", label: "Today", data: data.today },
    { key: "this_week", label: "This week", data: data.this_week },
    { key: "this_month", label: "This month", data: data.this_month },
    { key: "this_year", label: "This year", data: data.this_year },
  ];
  const maxTrend = Math.max(1, ...data.trend_14d.map((d) => d.total_usd));
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid="transactions-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Transaction log · money-in</p>
          <p className="serif text-2xl mt-1">
            {fmt(data.all_time.total_usd)} <span className="text-[#4A5C50] text-lg">all-time total · {data.all_time.count} transactions</span>
          </p>
        </div>
        <div className="text-right text-xs mono text-[#4A5C50]">
          <p>issuances: <span className="text-[#184A30]">{fmt(data.all_time.issuance_total_usd)}</span> · {data.all_time.issuance_count}</p>
          <p className="mt-1">top-ups: <span className="text-[#184A30]">{fmt(data.all_time.topup_total_usd)}</span> · {data.all_time.topup_count}</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {tiles.map((t) => (
          <div key={t.key} className="bg-[#FBF9F6] border border-[#E5E0D8] rounded-xl p-4" data-testid={`tx-tile-${t.key}`}>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">{t.label}</p>
            <p className="serif text-3xl mt-2 text-[#184A30]" data-testid={`tx-tile-${t.key}-total`}>{fmt(t.data.total_usd)}</p>
            <p className="text-xs mono text-[#4A5C50] mt-1">{t.data.count} txn{t.data.count === 1 ? "" : "s"}</p>
            <div className="mt-3 pt-3 border-t border-[#E5E0D8] text-[11px] mono text-[#4A5C50] space-y-0.5">
              <p className="flex justify-between"><span>issue</span><span className="text-[#184A30]">{fmt(t.data.issuance_total_usd)}</span></p>
              <p className="flex justify-between"><span>topup</span><span className="text-[#184A30]">{fmt(t.data.topup_total_usd)}</span></p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-[#E5E0D8]">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mb-3">Last 14 days · daily volume</p>
        <div className="flex items-end gap-1.5 h-24" data-testid="tx-trend">
          {data.trend_14d.map((d) => {
            const h = Math.round((d.total_usd / maxTrend) * 100);
            const label = new Date(d.date + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" });
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="w-full flex-1 flex items-end">
                  <div
                    className="w-full rounded-sm bg-[#184A30]/70 group-hover:bg-[#184A30] transition-colors"
                    style={{ height: `${Math.max(2, h)}%` }}
                    title={`${label} · ${fmt(d.total_usd)} · ${d.count} txn`}
                  />
                </div>
                <span className="text-[9px] text-[#4A5C50] mono whitespace-nowrap">{label.split(" ")[1]}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-[#E5E0D8]" data-testid="tx-csv-controls">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mb-3">Export CSV · accountant-ready</p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[10px] tracking-widest uppercase text-[#4A5C50]">From</label>
            <input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="block mt-1 px-3 py-2 rounded-lg border border-[#E5E0D8] bg-[#FBF9F6] mono text-sm"
              data-testid="tx-csv-since"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-widest uppercase text-[#4A5C50]">To</label>
            <input
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="block mt-1 px-3 py-2 rounded-lg border border-[#E5E0D8] bg-[#FBF9F6] mono text-sm"
              data-testid="tx-csv-until"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-widest uppercase text-[#4A5C50]">Type</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="block mt-1 px-3 py-2 rounded-lg border border-[#E5E0D8] bg-[#FBF9F6] mono text-sm"
              data-testid="tx-csv-kind"
            >
              <option value="all">All</option>
              <option value="issuance">Issuance only</option>
              <option value="topup">Top-up only</option>
            </select>
          </div>
          <button
            onClick={downloadCsv}
            disabled={downloading}
            className="h-10 px-5 rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-[#FBF9F6] text-sm font-medium disabled:opacity-50"
            data-testid="tx-csv-download"
          >
            {downloading ? "Preparing…" : "Download CSV"}
          </button>
          {(since || until || kind !== "all") && (
            <button
              onClick={() => { setSince(""); setUntil(""); setKind("all"); }}
              className="h-10 px-3 rounded-lg border border-[#E5E0D8] text-[#4A5C50] text-xs"
              data-testid="tx-csv-reset"
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
